import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X, BarChart, Star } from 'lucide-react';

const ResultsReview = ({
  questions,
  performanceData,
  onQuestionClick,
  onBackToDashboard
}) => {
  const navigate = useNavigate();
  const [showWrongOnly, setShowWrongOnly] = useState(false);
  
  // Calculate performance metrics
  const correctCount = questions.reduce((count, q) => 
    count + (q.isCorrect ? 1 : 0), 0);
  const totalAnswers = questions.length;
  const accuracy = totalAnswers > 0 ? Math.round((correctCount / totalAnswers) * 100) : 0;
  
  // Format time display from seconds
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const filteredQuestions = showWrongOnly 
    ? questions.filter(q => !q.isCorrect)
    : questions;

  const handleQuestionClick = (questionId) => {
    if (onQuestionClick) {
      onQuestionClick(questionId);
    }
  };

  // Define scoped variables for styling similar to Stats.css
  const styles = {
    // Color variables
    vars: {
      primary: '#9334E9',
      primaryDark: '#7828C8',
      light: '#E9D5FF',
      lightest: '#F5EEFF',
      background: '#F9FAFB',
      textPrimary: '#111827',
      textSecondary: '#6B7280',
      border: '#E5E7EB',
      white: '#FFFFFF',
      radiusLg: '12px',
      radiusMd: '8px',
      radiusSm: '6px',
      green: '#10B981',
      orange: '#F97316',
      red: '#EF4444'
    },

    container: {
      position: 'fixed',
      inset: 0,
      background: '#F9FAFB',
      overflowY: 'auto',
      paddingTop: '24px',
      zIndex: 1200,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
    },
    
    content: {
      maxWidth: '840px',
      margin: '0 auto',
      padding: '24px 32px',
      width: '100%'
    },
    
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
      marginBottom: '32px'
    },
    
    title: {
      fontSize: '24px',
      fontWeight: 600,
      color: '#111827',
      margin: 0,
      lineHeight: 1.2
    },
    
    section: {
      backgroundColor: '#FFFFFF',
      borderRadius: '12px',
      padding: '20px',
      marginBottom: '24px',
      border: '1px solid #E5E7EB',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
    },
    
    sectionTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '16px'
    },
    
    sectionIcon: {
      color: '#9334E9'
    },
    
    sectionTitleText: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#111827',
      margin: 0
    },
    
    summaryGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '24px'
    },
    
    summaryStat: {
      textAlign: 'center',
      padding: '16px',
      background: '#F5EEFF',
      borderRadius: '8px'
    },
    
    statHeader: {
      fontSize: '14px',
      fontWeight: 500,
      color: '#6B7280',
      marginBottom: '8px'
    },
    
    statValue: {
      fontSize: '32px',
      fontWeight: 700,
      color: '#9334E9',
      marginBottom: '4px'
    },
    
    statSubtitle: {
      fontSize: '12px',
      color: '#6B7280'
    },
    
    filterBox: {
      background: '#F5EEFF',
      borderRadius: '8px',
      padding: '12px 16px',
      fontSize: '14px',
      color: '#6B7280',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '16px'
    },
    
    questionsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '24px',
      marginBottom: '32px',
      width: '100%'
    },
    
    questionCard: {
      minHeight: '120px',
      padding: '16px',
      borderRadius: '8px',
      background: '#FFFFFF',
      border: '1px solid #E5E7EB',
      cursor: 'pointer',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
      transition: 'all 0.2s ease',
      position: 'relative'
    },
    
    questionCardHover: {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      borderColor: '#9334E9'
    },
    
    questionContent: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    },
    
    questionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '6px'
    },
    
    questionNumber: {
      fontSize: '13px',
      fontWeight: 500,
      color: '#6B7280'
    },
    
    questionStatus: {
      display: 'flex',
      alignItems: 'center'
    },
    
    questionText: {
      fontSize: '14px',
      fontWeight: 500,
      color: '#111827',
      lineHeight: 1.4,
      margin: '0 0 10px 0',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical'
    },
    
    correctAnswer: {
      fontSize: '12px',
      color: '#10B981',
      marginTop: 'auto'
    },
    
    buttonsContainer: {
      display: 'flex',
      gap: '16px',
      marginTop: '24px'
    },
    
    primaryButton: {
      flex: 1,
      backgroundColor: '#9334E9',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      padding: '12px 20px',
      fontSize: '14px',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
      boxShadow: '0 2px 4px rgba(147, 52, 233, 0.2)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    
    secondaryButton: {
      flex: 1,
      background: 'linear-gradient(135deg, #833AB4 0%, #FD1D1D 50%, #FCAF45 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      padding: '12px 20px',
      fontSize: '14px',
      fontWeight: 600, 
      cursor: 'pointer',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    
    progressBar: {
      height: '8px',
      background: '#f0f0f0',
      borderRadius: '6px',
      overflow: 'hidden',
      marginTop: '10px'
    },
    
    progressFill: {
      height: '100%',
      background: '#9334E9',
      borderRadius: '6px',
      transition: 'width 0.3s ease'
    }
  };
  
  // Responsive styles for mobile
  const mobileStyles = window.innerWidth <= 768 ? {
    content: {
      padding: '16px'
    },
    section: {
      padding: '16px'
    },
    summaryGrid: {
      gridTemplateColumns: '1fr',
      gap: '16px'
    },
    questionsGrid: {
      gridTemplateColumns: '1fr',
      gap: '16px',
      marginBottom: '24px'
    },
    buttonsContainer: {
      flexDirection: 'column'
    }
  } : {};

  // Merge mobile styles if needed
  Object.keys(mobileStyles).forEach(key => {
    if (mobileStyles[key]) {
      styles[key] = { ...styles[key], ...mobileStyles[key] };
    }
  });

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <div style={styles.header}>
          <h1 style={styles.title}>Study Results</h1>
        </div>
        
        {/* Performance Summary - Stats Style */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>
            <Star size={20} style={styles.sectionIcon} />
            <h2 style={styles.sectionTitleText}>Performance Summary</h2>
          </div>
          
          <div style={styles.summaryGrid}>
            <div style={styles.summaryStat}>
              <div style={styles.statHeader}>Accuracy</div>
              <div style={styles.statValue}>{accuracy}%</div>
              <div style={styles.statSubtitle}>{correctCount} of {totalAnswers} questions</div>
              <div style={styles.progressBar}>
                <div 
                  style={{
                    ...styles.progressFill,
                    width: `${accuracy}%`,
                    background: accuracy > 70 ? styles.vars.green : 
                               accuracy > 40 ? styles.vars.orange : 
                               styles.vars.red
                  }}
                ></div>
              </div>
            </div>
            
            <div style={styles.summaryStat}>
              <div style={styles.statHeader}>Time Spent</div>
              <div style={styles.statValue}>{formatTime(performanceData?.timeSpent || 0)}</div>
              <div style={styles.statSubtitle}>minutes:seconds</div>
            </div>
          </div>
        </div>
        
        {/* Questions Section */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>
            <BarChart size={20} style={styles.sectionIcon} />
            <h2 style={styles.sectionTitleText}>Question Analysis</h2>
          </div>
          
          <div style={styles.filterBox}>
            <input
              type="checkbox"
              id="wrongAnswers"
              checked={showWrongOnly}
              onChange={(e) => setShowWrongOnly(e.target.checked)}
              style={{ marginRight: '4px' }}
            />
            <label htmlFor="wrongAnswers">Show wrong answers only</label>
          </div>
          
          <div style={styles.questionsGrid}>
            {filteredQuestions.map((question, index) => (
              <div 
                key={question.id || question.question_id} 
                style={styles.questionCard}
                onClick={() => handleQuestionClick(question.id || question.question_id)}
                onMouseEnter={(e) => {
                  Object.assign(e.currentTarget.style, styles.questionCardHover);
                }}
                onMouseLeave={(e) => {
                  Object.assign(e.currentTarget.style, {
                    transform: 'none',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                    borderColor: '#E5E7EB'
                  });
                }}
              >
                <div style={styles.questionContent}>
                  <div style={styles.questionHeader}>
                    <span style={styles.questionNumber}>Question {index + 1}</span>
                    <div style={styles.questionStatus}>
                      {question.isCorrect ? (
                        <Check size={16} color={styles.vars.green} />
                      ) : (
                        <X size={16} color={styles.vars.red} />
                      )}
                    </div>
                  </div>
                  
                  <div style={styles.questionText}>
                    {question.text || question.question || ''}
                  </div>
                  
                  {!question.isCorrect && (
                    <div style={styles.correctAnswer}>
                      <span style={{ fontWeight: 500, marginRight: '4px' }}>Correct:</span>
                      <span>
                        {question.correctAnswer || 
                        (question.answers && question.answers[0]?.correctText) || 
                        'Not available'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          <div style={styles.buttonsContainer}>
            <button 
              style={styles.primaryButton}
              onClick={() => {
                if (onBackToDashboard) {
                  onBackToDashboard();
                } else {
                  // Check if we're in a diagnostic exam by looking at the URL
                  if (window.location.pathname.includes('/diagnostic-exam')) {
                    navigate('/review-personalized/diagnostic');
                  } else {
                    navigate('/');
                  }
                }
              }}
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultsReview;